// Phase R4 — Plan-then-act card + DeclinedPanel (design refactor).
//
// Visual port of the Claude Design `Pass2PlanCard` (extractor-art2)
// and `DeclinedPanel` (extractor-art-extras, the N09 first-class
// state) onto the se-* token namespace.
//
// Contract-frozen: PlanCardProps + every data-testid + the pure
// describeTool() logic are preserved verbatim. N09 honesty: the
// declined reason is engine-generated and rendered VERBATIM — the
// design's mock counter-offer/intent blocks are intentionally NOT
// fabricated (we only render data we actually have).

import type { FilterPlan, FilterTool } from "../../extractor/intentParser.ts";
import { SE } from "./seTokens.ts";

export interface PlanCardProps {
  plan: FilterPlan;
  /** User confirmed — Section B should execute the plan (SE-7). */
  onRun: () => void;
  /** User cancelled — return to the form. */
  onCancel: () => void;
  /** Disable buttons while parent submits to the Edge fn. */
  disabled?: boolean;
  /** NEL-B2 — when set, render the negotiated-session pass indicator
   *  ("Pass N of your negotiated extraction"). 1-based. */
  nelPassNumber?: number;
  /** NEL-B2 — projected combined odds after the proposed drops, for
   *  the pass indicator line. */
  nelProjectedOdds?: number | null;
}

export function PlanCard({
  plan,
  onRun,
  onCancel,
  disabled,
  nelPassNumber,
  nelProjectedOdds,
}: PlanCardProps): JSX.Element {
  // ─── N09 — honest refusal (DeclinedPanel design) ────────────
  if (plan.declined) {
    return (
      <div
        data-testid="sle-plan-card-declined"
        style={{
          background: SE.surfaceCard,
          border: `1px solid ${SE.border}`,
          borderLeft: `2px solid ${SE.conflictAmber}`,
          borderRadius: 4,
          padding: "28px 32px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            fontFamily: SE.fontMono,
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: SE.conflictAmber,
          }}
        >
          N09 · Plan declined · counter-offer
        </div>
        <div
          style={{
            fontFamily: SE.fontDisplay,
            fontSize: 32,
            fontWeight: 500,
            color: SE.text,
            marginTop: 14,
            letterSpacing: "-0.015em",
            lineHeight: 1.1,
          }}
        >
          We can&rsquo;t run this extraction.
        </div>
        <div
          style={{
            fontFamily: SE.fontBody,
            fontSize: 14,
            color: SE.textSec,
            marginTop: 14,
            maxWidth: 660,
            lineHeight: 1.65,
          }}
        >
          The system declined your request rather than approximate around
          it. The reason below is engine-generated and rendered verbatim.
        </div>

        <div
          style={{
            marginTop: 20,
            padding: "20px 22px",
            background: SE.surfacePage,
            border: `1px solid ${SE.border}`,
            borderLeft: `1px solid ${SE.dropRed}`,
          }}
        >
          <div
            style={{
              fontFamily: SE.fontMono,
              fontSize: 10,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: SE.dropRed,
            }}
          >
            Declined reason · verbatim
          </div>
          <div
            data-testid="sle-plan-card-declined-reason"
            style={{
              fontFamily: SE.fontBody,
              fontSize: 14,
              color: SE.text,
              marginTop: 8,
              lineHeight: 1.65,
              maxWidth: 760,
              whiteSpace: "pre-line",
            }}
          >
            {plan.decliningReason ??
              "The request isn't achievable from this slip. Try a different angle."}
          </div>
        </div>

        <div
          style={{
            marginTop: 24,
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            data-testid="sle-plan-card-declined-back"
            disabled={disabled}
            style={{
              padding: "11px 20px",
              border: `1px solid ${SE.border}`,
              background: "transparent",
              color: SE.textSec,
              fontFamily: SE.fontBody,
              fontSize: 13,
              fontWeight: 500,
              cursor: disabled ? "not-allowed" : "pointer",
              borderRadius: 2,
            }}
          >
            Rewrite my intent
          </button>
        </div>
      </div>
    );
  }

  // ─── Plan-then-act (Pass2PlanCard design) ───────────────────
  return (
    <div
      data-testid="sle-plan-card"
      style={{
        background: SE.surfaceCard,
        border: `1px solid ${SE.border}`,
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "20px 26px",
          borderBottom: `1px solid ${SE.border}`,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: SE.fontMono,
              fontSize: 10,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: SE.textMuted,
            }}
          >
            <span style={{ color: SE.textDim, marginRight: 12 }}>04</span>
            Plan-then-act
            {typeof nelPassNumber === "number" && nelPassNumber >= 1
              ? ` · pass ${nelPassNumber} proposal`
              : " proposal"}
          </div>
          {plan.summary && (
            <div
              data-testid="sle-plan-card-summary"
              style={{
                fontFamily: SE.fontDisplay,
                fontSize: 22,
                fontWeight: 500,
                color: SE.text,
                marginTop: 10,
                lineHeight: 1.2,
                letterSpacing: "-0.005em",
              }}
            >
              {plan.summary}
            </div>
          )}
          <div
            style={{
              fontFamily: SE.fontBody,
              fontSize: 12.5,
              color: SE.textSec,
              marginTop: 6,
            }}
          >
            N09 · plan-then-act. The system never executes without your
            confirmation.
          </div>
          {typeof nelPassNumber === "number" && nelPassNumber >= 1 && (
            <div
              data-testid="sle-plan-card-nel-pass"
              style={{
                fontFamily: SE.fontMono,
                fontSize: 10.5,
                letterSpacing: "0.10em",
                color: SE.textSec,
                borderLeft: `2px solid ${SE.amber}`,
                paddingLeft: 10,
                marginTop: 10,
              }}
            >
              Pass {nelPassNumber} of your negotiated extraction
            </div>
          )}
        </div>
        {typeof nelProjectedOdds === "number" && nelProjectedOdds > 0 && (
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div
              style={{
                fontFamily: SE.fontMono,
                fontSize: 9.5,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: SE.textMuted,
              }}
            >
              Projected combined
            </div>
            <div
              style={{
                fontFamily: SE.fontDisplay,
                fontSize: 24,
                fontWeight: 500,
                color: SE.text,
                marginTop: 6,
              }}
            >
              {nelProjectedOdds.toFixed(nelProjectedOdds >= 100 ? 0 : 2)}×
            </div>
            <div
              style={{
                fontFamily: SE.fontMono,
                fontSize: 9.5,
                color: SE.textMuted,
                marginTop: 2,
              }}
            >
              after this pass
            </div>
          </div>
        )}
      </div>

      {/* Steps */}
      {plan.tools.length === 0 ? (
        <div
          data-testid="sle-plan-card-empty"
          style={{
            padding: "16px 26px",
            fontFamily: SE.fontMono,
            fontSize: 11,
            color: SE.textMuted,
          }}
        >
          (no filtering steps — the plan keeps every leg as-is)
        </div>
      ) : (
        <ol
          data-testid="sle-plan-card-steps"
          style={{ margin: 0, padding: 0, listStyle: "none" }}
        >
          {plan.tools.map((tool, i) => (
            <li
              key={i}
              data-testid={`sle-plan-step-${i}`}
              data-tool={tool.name}
              style={{
                padding: "13px 26px",
                borderTop: i > 0 ? `1px solid ${SE.border}` : "none",
                display: "grid",
                gridTemplateColumns: "28px 1fr",
                gap: 14,
                alignItems: "baseline",
              }}
            >
              <span
                style={{
                  fontFamily: SE.fontMono,
                  fontSize: 12,
                  color: SE.textMuted,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                style={{
                  fontFamily: SE.fontBody,
                  fontSize: 13,
                  color: SE.text,
                  lineHeight: 1.5,
                }}
              >
                {describeTool(tool)}
              </span>
            </li>
          ))}
        </ol>
      )}

      {/* CTA bar */}
      <div
        style={{
          padding: "16px 26px",
          borderTop: `1px solid ${SE.border}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          data-testid="sle-plan-card-cancel"
          style={{
            padding: "11px 20px",
            border: `1px solid ${SE.border}`,
            background: "transparent",
            color: SE.textSec,
            fontFamily: SE.fontBody,
            fontSize: 13,
            fontWeight: 500,
            cursor: disabled ? "not-allowed" : "pointer",
            borderRadius: 2,
          }}
        >
          {typeof nelPassNumber === "number" && nelPassNumber > 1
            ? "Cancel · stay at pass 1"
            : "Cancel"}
        </button>
        <button
          type="button"
          onClick={onRun}
          disabled={disabled || plan.tools.length === 0}
          data-testid="sle-plan-card-run"
          style={{
            padding: "11px 22px",
            border: "none",
            background: disabled || plan.tools.length === 0
              ? SE.surfaceRaised
              : SE.amber,
            color: disabled || plan.tools.length === 0
              ? SE.textMuted
              : SE.surfacePage,
            fontFamily: SE.fontBody,
            fontSize: 13.5,
            fontWeight: 600,
            cursor: disabled || plan.tools.length === 0
              ? "not-allowed"
              : "pointer",
            borderRadius: 2,
          }}
        >
          {typeof nelPassNumber === "number" && nelPassNumber >= 1
            ? `Run pass ${nelPassNumber}`
            : "Run plan"}
        </button>
      </div>
    </div>
  );
}

/** Pure helper: render one tool as a single human-readable sentence.
 *  Exported for unit testing. */
export function describeTool(tool: FilterTool): string {
  switch (tool.name) {
    case "filter_by_market": {
      const parts: string[] = [];
      if (tool.include?.length) {
        parts.push(`keep ${joinList(tool.include)} markets`);
      }
      if (tool.exclude?.length) {
        parts.push(`drop ${joinList(tool.exclude)} markets`);
      }
      return parts.length > 0
        ? capitalize(parts.join("; "))
        : "Filter by market (no targets given).";
    }
    case "filter_by_odds_range": {
      if (tool.min !== undefined && tool.max !== undefined) {
        return `Keep legs priced between ${tool.min.toFixed(2)} and ${tool.max.toFixed(2)}.`;
      }
      if (tool.min !== undefined) {
        return `Keep legs priced at ${tool.min.toFixed(2)} or higher.`;
      }
      if (tool.max !== undefined) {
        return `Keep legs priced at ${tool.max.toFixed(2)} or lower.`;
      }
      return "Filter by odds range (no bounds given).";
    }
    case "filter_by_league": {
      const parts: string[] = [];
      if (tool.include?.length) parts.push(`keep ${joinList(tool.include)}`);
      if (tool.exclude?.length) parts.push(`drop ${joinList(tool.exclude)}`);
      return parts.length > 0
        ? capitalize(parts.join("; "))
        : "Filter by league (no targets given).";
    }
    case "filter_by_predicted_hit_probability":
      return `Drop legs the scorer rates below ${(tool.min * 100).toFixed(0)}% win probability.`;
    case "filter_by_predicted_ev":
      return `Drop legs whose expected value is below ${(tool.min * 100).toFixed(1)}%.`;
    case "select_top_n_by":
      return `Keep only the top ${tool.n} legs by ${labelFor(tool.criterion)}.`;
    case "cap_combined_odds":
      return `Cap combined odds at ${tool.max.toFixed(1)}.`;
    case "target_combined_odds":
      return `Target combined odds around ${tool.value.toFixed(1)} (±${(tool.tolerance * 100).toFixed(0)}%).`;
    case "cap_leg_count":
      return `Keep no more than ${tool.max} legs.`;
    case "filter_out_low_confidence":
      return tool.enabled
        ? "Drop legs where the scorer's confidence is low."
        : "Keep low-confidence legs (override default).";
    case "prune_risky_legs": {
      const bits: string[] = [];
      if (typeof tool.max_fraction === "number") {
        bits.push(`removing at most ${Math.round(tool.max_fraction * 100)}%`);
      }
      // Only surface min_keep when the user RAISED it above the default safety
      // floor (4). Echoing the default reads as a leg count the user never
      // asked for (e.g. "at least 4 legs" on a pure "return at least 80 odds").
      if (typeof tool.min_keep === "number" && tool.min_keep > 4) {
        bits.push(`keeping at least ${tool.min_keep}`);
      }
      const qual = bits.length > 0 ? ` (${bits.join(", ")})` : "";
      return `Remove the legs most likely to lose this slip${qual} — the rest are kept.`;
    }
    case "set_optimization_target":
      switch (tool.kind) {
        case "max_ev":
          return "Optimise for the highest expected-value subset.";
        case "max_hit_prob":
          return "Optimise for the highest combined hit probability.";
        case "closest_to_odds":
          return `Optimise to land combined odds as close to ${tool.value?.toFixed(1)} as possible.`;
      }
      return "Set optimisation target.";
    case "decline":
      return tool.reason;
  }
}

function labelFor(c: "pWin" | "ev" | "odds"): string {
  switch (c) {
    case "pWin":
      return "win probability";
    case "ev":
      return "expected value";
    case "odds":
      return "decimal odds";
  }
}

function joinList(arr: string[]): string {
  if (arr.length <= 1) return arr.join("");
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(", ")}, and ${arr[arr.length - 1]}`;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1) + ".";
}
